import React, { useContext, useEffect, useRef } from "react";
import { CopyIcon, DeleteIcon, EditIcon, OverFlowIcon } from "assests/GmsIcons";
import { formatDateWithTime } from "Common/HelperClass";
import ExpandCollapseIcon from "Components/Common/components/ExpandCollapseIcon";
import { useState } from "react";
import TableSkeletonElement from "Components/GMS/Products/components/TableSkeletonElement.js";
import ChipStatus from "Pages/GMS/ARS/Components/ChipStatus";
import TdElement from "./TdElement";
import OverflowOption from "./OverflowOption";
import { PivotSortableColumn } from "../constants/CSConstants";
import { ArsContext } from "Pages/GMS/ARS/Contexts/ArsContext";
import { formatTableData } from "../Helpers/CSHelper";
import { toast } from "react-toastify";

function CSTable({ pivotData, setPivotData, loading, handleDelete, handleEditAndCreateVersion, NoDataComponent, handleDefaultVersionSelection, newlyCreatedId, isSidePanel, setRemoveDefaultId }: CSTableProps) {
    const [sortColumn, setSortColumn] = useState<PivotSortableColumn | null>(null);
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    const [selectIndex, setSelectIndex] = useState<SelectIndex>({ defaultIndex: null, versionIndex: null });
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const menuRef = useRef<HTMLDivElement | null>(null);
    const { model, activeSubmodelIndex } = useContext(ArsContext);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setSelectIndex({ defaultIndex: null, versionIndex: null });
            }
        }
        function handleScroll() {
            if (selectIndex.defaultIndex !== null || selectIndex.versionIndex !== null) {
                setSelectIndex({ defaultIndex: null, versionIndex: null });
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [selectIndex]);

    const handleOverflowClick = (event: React.MouseEvent<HTMLDivElement>, defaultIndex: number, versionIndex: number | null) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom - window.scrollY,
            left: rect.left - window.scrollX,
        });
        setSelectIndex({ defaultIndex, versionIndex });
    };

    const sortedData = pivotData?.data?.pivot_attributes?.sort((a, b) => {
        if (sortColumn) {
            const aValue = a[sortColumn];
            const bValue = b[sortColumn];

            if (typeof aValue === "number" && typeof bValue === "number") {
                return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                const aStr = String(aValue).toLowerCase();
                const bStr = String(bValue).toLowerCase();
                return sortDirection === "asc"
                    ? aStr.localeCompare(bStr)
                    : bStr.localeCompare(aStr);
            }
        }
        return 0;
    });

    const formattedData = isSidePanel && model?.submodel[activeSubmodelIndex]?.pivots ? formatTableData(sortedData, model?.submodel[activeSubmodelIndex]?.pivots) : sortedData;

    const handleDefaultClick = (version: PivotVersion, removeDefault?: string | null) => {
        handleDefaultVersionSelection(version);
        if (isSidePanel && removeDefault) {
            setRemoveDefaultId(removeDefault);
        }
    };


    const handleSort = (column: PivotSortableColumn) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    const deleteFunction = async (selectIndex: SelectIndex) => {
        handleDelete(selectIndex)
    }

    const sortElement = (column: string) => {
        return sortColumn === column ? (
            <span className="tw-ml-1">
                {sortDirection === "asc" ? (
                    <i className="fas fa-sort-up" />
                ) : (
                    <i className="fas fa-sort-down" />
                )}
            </span>
        ) : (
            <i className="fas fa-sort" />
        );
    };

    const toggleExpanded = (index: number) => {
        setPivotData(prevData => ({
            ...prevData,
            data: {
                ...prevData.data,
                pivot_attributes: prevData.data.pivot_attributes.map((item, i) =>
                    i === index ? { ...item, expand: !item.expand } : item
                )
            }
        }));
    };

    const getRadioCheckedValue = (responseItem: PivotBaseItem) => {
        const pivots = model?.submodel[activeSubmodelIndex]?.pivots;

        if (!pivots || Object.keys(pivots).length === 0) {
            return responseItem.default;
        }

        return pivots.includes(responseItem.uid);
    };

    const headerClasses = "tw-px-6 tw-cursor-pointer tw-py-3 tw-text-left tw-text-sm tw-font-semibold tw-text-[#6B778C] tw-uppercase tw-tracking-wider tw-whitespace-nowrap";

    const renderHeaderCell = (column: PivotSortableColumn, label: string, indent: boolean = false) => (
        <th key={column} className={headerClasses} onClick={() => handleSort(column)}>
            <div className={`tw-flex tw-items-center ${indent ? 'tw-ml-2' : ''}`}>
                <span>{label}</span>
                <span className="tw-ml-1">{sortElement(column)}</span>
            </div>
        </th>
    );

    if (loading) { return <div className=""><TableSkeletonElement /></div> }

    if (loading === false && pivotData?.data?.pivot_attributes?.length === 0) { return <div><NoDataComponent /></div> }

    return (
        <div className="tw-border tw-border-solid tw-mt-3 tw-border-[#DFE0E6] tw-border-r-2 ">
            <div className="table-container" style={{ width: '100%', height: 'auto', overflowX: 'auto' }}>
                <table className="tw-bg-[#DFE1E6] tw-w-full">
                    <thead className="tw-sticky tw-top-0 tw-bg-[#DFE1E6] tw-z-50">
                        <tr>
                            <th key={PivotSortableColumn.CATEGORY} className={`${headerClasses} tw-sticky tw-left-0 tw-bg-[#DFE1E6]`} onClick={() => handleSort(PivotSortableColumn.CATEGORY)}><div className={`tw-flex tw-items-center tw-ml-2`}><span>{"Category"}</span><span className="tw-ml-1">{sortElement(PivotSortableColumn.CATEGORY)}</span></div></th>
                            {renderHeaderCell(PivotSortableColumn.VERSION, 'Identifier')}
                            <th key="STATUS" className={headerClasses}><div className="tw-flex tw-items-center"><span>STATUS</span></div></th>
                            {renderHeaderCell(PivotSortableColumn.ATTRIBUTES, 'Attributes')}
                            {renderHeaderCell(PivotSortableColumn.CREATED_ON, 'Created on')}
                            {renderHeaderCell(PivotSortableColumn.LAST_UPDATED_BY, 'Last updated by')}
                            <th key="Default" className={headerClasses}><div className="tw-flex tw-items-center"><span>Default</span></div></th>
                            {!isSidePanel && <th className="table-header-style tw-w-4 tw-mr-2"></th>}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {
                            formattedData?.map((responseItem, defaultIndex) => {
                                return <>
                                    {
                                        <tr key={responseItem.uid} className="hover:bg-gray-100">
                                            {TdElement({ defaultIndex: defaultIndex, versionIndex: null },
                                                <div className="tw-flex tw-items-center tw-justify-start">
                                                    <div className="tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center"> {responseItem?.versions?.length >= 1 ? (<ExpandCollapseIcon expand={responseItem.expand} toggleExpanded={toggleExpanded} index={defaultIndex} />) : <div className="tw-w-6 tw-h-6" />}</div>
                                                    <div className="tw-text-blue-700 tw-text-sm  tw-leading-tight">{responseItem?.category_name ?? ""}</div>
                                                </div>, true, true
                                            )}
                                            {TdElement({ defaultIndex: defaultIndex, versionIndex: null }, <div className="tw-text-blue-950 tw-text-sm  tw-leading-tight">{responseItem.version_name}{newlyCreatedId === responseItem.uid ? <span className="tw-w-[38px] tw-h-4 tw-bg-[#172b4d] tw-rounded-[26px] tw-px-3 tw-py-1 tw-ml-3 tw-text-white tw-text-xs  tw-leading-tight">New</span> : ""}</div>)}
                                            {TdElement({ defaultIndex: defaultIndex, versionIndex: null }, <ChipStatus status={responseItem.status.toLowerCase() === "live"} />)}
                                            {TdElement({ defaultIndex: defaultIndex, versionIndex: null }, <div className="tw-text-blue-950 tw-text-sm  tw-leading-tight tw-overflow-hidden tw-truncate w-2">{responseItem.attribute}</div>)}
                                            {TdElement({ defaultIndex: defaultIndex, versionIndex: null }, formatDateWithTime(responseItem?.created_on) || "")}
                                            {TdElement({ defaultIndex: defaultIndex, versionIndex: null }, responseItem?.last_updated_by?.username)}
                                            {TdElement({ defaultIndex: defaultIndex, versionIndex: null }, <input type="radio" name={`radio-group-${defaultIndex}`} checked={!isSidePanel ? responseItem.default : getRadioCheckedValue(responseItem)} onChange={() => handleDefaultClick(responseItem)} />)}
                                            {!isSidePanel && TdElement({ defaultIndex: defaultIndex, versionIndex: null },
                                                <>
                                                    <div className="tw-cursor-pointer tw-py-2 tw-px-3 tw-rounded-lg hover:tw-bg-shades-of-grey-20"
                                                        onClick={(e) => {
                                                            if (defaultIndex === selectIndex.defaultIndex && selectIndex.versionIndex === null) setSelectIndex({ defaultIndex: null, versionIndex: null });
                                                            else { handleOverflowClick(e, defaultIndex, null); }
                                                        }}>
                                                        <OverFlowIcon />
                                                    </div>

                                                    {defaultIndex === selectIndex.defaultIndex && selectIndex.versionIndex === null ? (
                                                        <div ref={menuRef}
                                                            className="tw-fixed tw-rounded-[4px] tw-z-50  tw-bg-white tw-ring-1 tw-ring-[#EBECF0]"
                                                            style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left + 40}px` }}>
                                                            <div className={`tw-origin-top-left tw-mr-8 tw-right-0 tw-absolute  tw-mx-3 tw-rounded-[4px] tw-z-50 tw-bg-white tw-ring-1 tw-ring-[#EBECF0]`}>
                                                                {OverflowOption(responseItem, <EditIcon />, "Edit", () => { setSelectIndex({ defaultIndex: null, versionIndex: null }); handleEditAndCreateVersion(selectIndex, "edit") })}
                                                                {OverflowOption(responseItem, <CopyIcon />, "Create Version", () => { setSelectIndex({ defaultIndex: null, versionIndex: null }); handleEditAndCreateVersion(selectIndex, "create_version") })}
                                                                {OverflowOption(responseItem, <DeleteIcon />, "Delete", () => { setSelectIndex({ defaultIndex: null, versionIndex: null }); if (responseItem?.versions && responseItem.versions.length > 0) { toast.error("You can’t delete this substitution because it has versions") } else { deleteFunction(selectIndex) } })}
                                                            </div></div>
                                                    ) : null}
                                                </>
                                            )}
                                        </tr>
                                    }

                                    {/* Versions */}

                                    {
                                        responseItem?.versions?.length >= 1 && responseItem?.expand && responseItem?.versions?.map((version: PivotBaseItem, versionIndex: number) => (
                                            <tr key={version?.uid} className="hover:bg-gray-100">
                                                {TdElement({ defaultIndex: defaultIndex, versionIndex: versionIndex }, <div className="tw-text-blue-700 tw-text-sm  tw-leading-tight"></div>, true, true)}
                                                {TdElement({ defaultIndex: defaultIndex, versionIndex: versionIndex }, <div className="tw-text-blue-950 tw-text-sm  tw-leading-tight">{version.version_name}{newlyCreatedId === version.uid ? <span className="tw-w-[38px] tw-h-4 tw-bg-[#172b4d] tw-rounded-[26px] tw-px-3 tw-py-1 tw-ml-3 tw-text-white tw-text-xs  tw-leading-tight">New</span> : ""}</div>)}
                                                {TdElement({ defaultIndex: defaultIndex, versionIndex: versionIndex }, <ChipStatus status={version.status.toLowerCase() === "live"} />)}
                                                {TdElement({ defaultIndex: defaultIndex, versionIndex: versionIndex }, <div className="tw-text-blue-950 tw-text-sm  tw-leading-tight tw-overflow-hidden tw-truncate w-2">{version.attribute}</div>)}
                                                {TdElement({ defaultIndex: defaultIndex, versionIndex: versionIndex }, formatDateWithTime(version?.created_on) || "")}
                                                {TdElement({ defaultIndex: defaultIndex, versionIndex: versionIndex }, version?.last_updated_by?.username)}
                                                {TdElement({ defaultIndex: defaultIndex, versionIndex: versionIndex }, <input type="radio" name={`radio-group-${defaultIndex},${versionIndex}`} checked={!isSidePanel ? version.default : getRadioCheckedValue(version)} onChange={() => handleDefaultClick(version, responseItem.uid)} />)}
                                                {!isSidePanel && TdElement({ defaultIndex: defaultIndex, versionIndex: versionIndex },
                                                    <>
                                                        <div className="tw-cursor-pointer tw-py-2 tw-px-3 tw-rounded-lg hover:tw-bg-shades-of-grey-20"
                                                            onClick={(e) => {
                                                                if (defaultIndex === selectIndex.defaultIndex && versionIndex === selectIndex.versionIndex) setSelectIndex({ defaultIndex: null, versionIndex: null });
                                                                else { handleOverflowClick(e, defaultIndex, versionIndex); }
                                                            }} >
                                                            <OverFlowIcon />
                                                        </div>
                                                        <div className="tw-fixed  tw-rounded-[4px] tw-z-50 tw-bg-white tw-ring-1 tw-ring-[#EBECF0]"
                                                            style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left + 40}px` }}>
                                                            {defaultIndex === selectIndex.defaultIndex && versionIndex === selectIndex.versionIndex && (
                                                                <div ref={menuRef} className={`tw-origin-top-left tw-mr-8 tw-right-0 tw-absolute  tw-mx-3 tw-rounded-[4px] tw-z-50 tw-bg-white tw-ring-1 tw-ring-[#EBECF0]`}>
                                                                    {OverflowOption(version, <EditIcon />, "Edit", () => { setSelectIndex({ defaultIndex: null, versionIndex: null }); handleEditAndCreateVersion(selectIndex, "edit_version") })}
                                                                    {OverflowOption(version, <CopyIcon />, "Create Version", () => { setSelectIndex({ defaultIndex: null, versionIndex: null }); handleEditAndCreateVersion(selectIndex, "create_version") })}
                                                                    {OverflowOption(version, <DeleteIcon />, "Delete", () => { setSelectIndex({ defaultIndex: null, versionIndex: null }); deleteFunction(selectIndex) })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                </>
                            })
                        }
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default CSTable